import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MagnifyingGlass, X, ArrowRight, Package, User as UserIcon,
         ShoppingCart, ClipboardText, FileText, Truck, Wrench, Users as UsersIcon,
         Receipt, Buildings, Money } from "@phosphor-icons/react";

const KIND_META = {
  "inventory":       { icon: Package,        label: "Item",      color: "#0891b2" },
  "employee":        { icon: UserIcon,       label: "Employee",  color: "#7c3aed" },
  "purchase-order":  { icon: ShoppingCart,   label: "PO",        color: "#059669" },
  "quotation":       { icon: FileText,       label: "Quotation", color: "#0284c7" },
  "order":           { icon: FileText,       label: "Order",     color: "#0284c7" },
  "request":         { icon: ClipboardText,  label: "Request",   color: "#d97706" },
  "delivery-note":   { icon: FileText,       label: "DN",        color: "#0284c7" },
  "invoice":         { icon: Receipt,        label: "Invoice",   color: "#be185d" },
  "customer":        { icon: UsersIcon,      label: "Customer",  color: "#0284c7" },
  "supplier":        { icon: Buildings,      label: "Supplier",  color: "#4338ca" },
  "machine":         { icon: Wrench,         label: "Machine",   color: "#b45309" },
  "truck":           { icon: Truck,          label: "Truck",     color: "#0f766e" },
  "project":         { icon: Money,          label: "Project",   color: "#7c2d12" },
};

function debounce(fn, ms) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
}

export default function CommandPalette({ open, onClose }) {
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open]);

  const runSearch = useMemo(() => debounce(async (q) => {
    if (!q || q.trim().length < 1) { setResults([]); setLoading(false); return; }
    try {
      const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data.results || []);
      setActiveIdx(0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, 180), []);

  useEffect(() => {
    if (!open) return;
    setLoading(!!query);
    runSearch(query);
  }, [query, open, runSearch]);

  function pick(row) {
    if (!row?.url) return;
    onClose?.();
    nav(row.url);
  }

  function onKey(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter")     { e.preventDefault(); pick(results[activeIdx]); }
    else if (e.key === "Escape")    { onClose?.(); }
  }

  // Scroll active row into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 pt-24"
      onClick={onClose}
      data-testid="command-palette"
    >
      <div
        className="w-full max-w-2xl bg-white shadow-2xl border border-black/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#e4e4e7]">
          <MagnifyingGlass size={20} weight="bold" className="text-[#71717a]" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search inventory, POs, employees, machines, customers…"
            className="flex-1 outline-none text-lg bg-transparent"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline text-[10px] text-[#71717a] border border-[#e4e4e7] px-1.5 py-0.5">ESC</kbd>
          <button onClick={onClose} className="text-[#71717a] hover:text-black" data-testid="command-palette-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto" ref={listRef} data-testid="command-palette-results">
          {!query && (
            <div className="p-6 text-center text-sm text-[#71717a]">
              Start typing to search across every module.<br />
              <span className="text-xs">Tip: press <kbd className="border border-[#e4e4e7] px-1">↑ ↓</kbd> to navigate · <kbd className="border border-[#e4e4e7] px-1">↵</kbd> to open.</span>
            </div>
          )}
          {query && loading && (
            <div className="p-4 text-sm text-[#71717a]">Searching…</div>
          )}
          {query && !loading && results.length === 0 && (
            <div className="p-6 text-center text-sm text-[#71717a]">
              No matches for <span className="mono">{query}</span>
            </div>
          )}
          {results.map((r, idx) => {
            const meta = KIND_META[r.kind] || { icon: FileText, label: r.kind, color: "#52525b" };
            const Icon = meta.icon;
            const active = idx === activeIdx;
            return (
              <button
                key={`${r.kind}-${r.id || idx}`}
                type="button"
                data-idx={idx}
                data-testid={`command-palette-item-${idx}`}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => pick(r)}
                className={`w-full flex items-center gap-3 px-5 py-3 border-b border-[#f4f4f5] text-left ${active ? "bg-[#f4f4f5]" : "bg-white"}`}
              >
                <div
                  className="w-9 h-9 flex items-center justify-center text-white shrink-0"
                  style={{ background: meta.color }}
                >
                  <Icon size={16} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{r.title}</div>
                  {r.subtitle && (
                    <div className="text-xs text-[#71717a] truncate mono">{r.subtitle}</div>
                  )}
                </div>
                <span className="phizo-label text-[9px] tracking-wider text-[#71717a] border border-[#e4e4e7] px-1.5 py-0.5">
                  {meta.label}
                </span>
                <ArrowRight size={14} weight="bold" className={active ? "text-black" : "text-[#a1a1aa]"} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
